import React, { useState, useEffect, useCallback } from "react";
import apiClient from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Users, Shield, KeyRound, Eye, EyeOff, UserPlus, Check, X } from "lucide-react";
import { formatDateEU } from "@/utils/dateFormat";

const ROLE_OPTIONS = [
  { value: "COACH", label: "Coach", desc: "Inscription + Entrée Caisse" },
  { value: "RESPONSABLE", label: "Responsable", desc: "Dashboard + Membres + Échéances + Rapports" },
  { value: "ADMIN", label: "Administrateur", desc: "Accès complet" },
];

const ROLE_COLORS = {
  ADMIN: "bg-purple-100 text-purple-700",
  RESPONSABLE: "bg-blue-100 text-blue-700",
  COACH: "bg-gray-100 text-gray-600",
};

const ROLE_LABELS = { ADMIN: "Administrateur", RESPONSABLE: "Responsable", COACH: "Coach" };

const SettingsPage = () => {
  const [users, setUsers] = useState([]);
  const [accessRequests, setAccessRequests] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [pwdUserId, setPwdUserId] = useState(null);
  const [pwdUserName, setPwdUserName] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "COACH" });
  const [approveRole, setApproveRole] = useState({});
  const { isAdmin, user: currentUser } = useAuth();

  const fetchUsers = useCallback(() => {
    if (isAdmin) {
      apiClient.get("/auth/users").then((r) => setUsers(r.data)).catch(console.error);
    }
  }, [isAdmin]);

  const fetchAccessRequests = useCallback(() => {
    if (isAdmin) {
      apiClient.get("/auth/access-requests").then((r) => setAccessRequests(r.data)).catch(console.error);
    }
  }, [isAdmin]);

  useEffect(() => { fetchUsers(); fetchAccessRequests(); }, [fetchUsers, fetchAccessRequests]);

  const handleAdd = async () => {
    if (!form.email || !form.password) return;
    try {
      await apiClient.post("/auth/users", form);
      fetchUsers();
      setShowAdd(false);
      setForm({ email: "", password: "", name: "", role: "COACH" });
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur lors de la création");
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Supprimer l'utilisateur "${name}" ?\nIl perdra tout accès au système.`)) return;
    try {
      await apiClient.delete(`/auth/users/${id}`);
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur");
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await apiClient.put(`/auth/users/${userId}`, { role: newRole });
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur");
    }
  };

  const openPasswordModal = (userId, userName) => {
    setPwdUserId(userId);
    setPwdUserName(userName);
    setNewPassword("");
    setShowPwd(true);
  };

  const handlePasswordReset = async () => {
    if (!newPassword || newPassword.length < 6) {
      alert("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }
    try {
      await apiClient.put(`/auth/users/${pwdUserId}`, { password: newPassword });
      setShowPwd(false);
      alert("Mot de passe mis à jour");
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur");
    }
  };

  const handleApprove = async (reqId) => {
    const role = approveRole[reqId] || "COACH";
    try {
      await apiClient.post(`/auth/access-requests/${reqId}/approve`, { role });
      fetchAccessRequests();
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur lors de la validation");
    }
  };

  const handleReject = async (reqId, name) => {
    if (!window.confirm(`Refuser la demande de "${name}" ?`)) return;
    try {
      await apiClient.post(`/auth/access-requests/${reqId}/reject`);
      fetchAccessRequests();
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur");
    }
  };

  if (!isAdmin) return <div className="crm-page"><p className="text-[#577083]">Accès réservé à l'administrateur</p></div>;

  return (
    <div className="crm-page">
      <div className="crm-page-header">
        <div>
          <h1 className="crm-page-title">Paramètres</h1>
          <p className="crm-page-subtitle">Gestion des utilisateurs et niveaux d'accès</p>
        </div>
      </div>

      {/* Role legend */}
      <div className="bg-white rounded-xl border border-[#edf0f4] p-4 mb-6">
        <h3 className="text-sm font-semibold text-[#162d3d] mb-3 flex items-center gap-2"><Shield size={16} /> Niveaux d'accréditation</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {ROLE_OPTIONS.map(r => (
            <div key={r.value} className="flex items-start gap-3 p-3 rounded-lg bg-[#f4f7fa]">
              <span className={`crm-badge ${ROLE_COLORS[r.value]} text-xs mt-0.5`}>{r.label}</span>
              <span className="text-xs text-[#577083]">{r.desc}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Access requests */}
      {accessRequests.length > 0 && (
        <div className="crm-card mb-6 border-l-4 border-l-[#f59e0b]">
          <div className="flex items-center gap-2 mb-4">
            <UserPlus size={18} className="text-[#f59e0b]" />
            <h3 className="crm-card-title mb-0">Demandes d'accès en attente ({accessRequests.length})</h3>
          </div>
          <div className="crm-table-wrap" style={{boxShadow:'none'}}>
            <table className="crm-table">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Identifiant</th>
                  <th>Date</th>
                  <th>Rôle à attribuer</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {accessRequests.map((req) => (
                  <tr key={req.id} data-testid={`access-req-${req.id}`}>
                    <td className="font-medium">{req.last_name} {req.first_name}</td>
                    <td className="text-[#577083]">{req.login}</td>
                    <td className="text-[#577083] text-xs">{formatDateEU(req.created_at)}</td>
                    <td>
                      <Select value={approveRole[req.id] || "COACH"} onValueChange={(val) => setApproveRole({...approveRole, [req.id]: val})}>
                        <SelectTrigger className="w-[160px] h-8 text-xs" data-testid={`approve-role-${req.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="z-[200]">
                          {ROLE_OPTIONS.map(r => (
                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        <Button size="sm" onClick={() => handleApprove(req.id)} className="bg-green-600 hover:bg-green-700 text-white h-8 text-xs" data-testid={`approve-btn-${req.id}`}>
                          <Check size={14} className="mr-1" /> Valider
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleReject(req.id, `${req.first_name} ${req.last_name}`)} className="text-red-400 hover:text-red-600 h-8 text-xs" data-testid={`reject-btn-${req.id}`}>
                          <X size={14} className="mr-1" /> Refuser
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="crm-card mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users size={18} className="text-[#577083]" />
            <h3 className="crm-card-title mb-0">Utilisateurs ({users.length})</h3>
          </div>
          <Button size="sm" onClick={() => setShowAdd(true)} className="bg-[#1a2744] hover:bg-[#243459] text-[#e8d44d]" data-testid="add-user-btn">
            <Plus size={14} className="mr-1" /> Ajouter
          </Button>
        </div>
        <div className="crm-table-wrap" style={{boxShadow:'none'}}>
          <table className="crm-table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Identifiant</th>
                <th>Rôle</th>
                <th>Dernière connexion</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="font-medium">{u.name || "—"}</td>
                  <td>{u.email}</td>
                  <td>
                    {u.id === currentUser?.id ? (
                      <span className={`crm-badge ${ROLE_COLORS[u.role]} text-xs`}>
                        <Shield size={12} className="inline mr-1" />{ROLE_LABELS[u.role]}
                      </span>
                    ) : (
                      <Select value={u.role} onValueChange={(val) => handleRoleChange(u.id, val)}>
                        <SelectTrigger className="w-[160px] h-8 text-xs" data-testid={`role-select-${u.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="z-[200]">
                          {ROLE_OPTIONS.map(r => (
                            <SelectItem key={r.value} value={r.value}>
                              <span className="flex items-center gap-2">
                                <span className={`inline-block w-2 h-2 rounded-full ${r.value === 'ADMIN' ? 'bg-purple-500' : r.value === 'RESPONSABLE' ? 'bg-blue-500' : 'bg-gray-400'}`} />
                                {r.label}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </td>
                  <td className="text-[#577083]">{u.last_login ? formatDateEU(u.last_login) : "Jamais"}</td>
                  <td>
                    <div className="flex items-center gap-1">
                      {u.id !== currentUser?.id && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => openPasswordModal(u.id, u.name || u.email)} title="Changer le mot de passe" data-testid={`pwd-btn-${u.id}`}>
                            <KeyRound size={14} className="text-[#3899ec]" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(u.id, u.name || u.email)} title="Supprimer" data-testid={`delete-btn-${u.id}`}>
                            <Trash2 size={14} className="text-red-400" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add user modal */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouvel utilisateur</DialogTitle></DialogHeader>
          <div className="flex flex-col gap-3 py-2">
            <Input placeholder="Nom complet" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="new-user-name" />
            <Input placeholder="Identifiant (email ou pseudo) *" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="new-user-email" />
            <Input placeholder="Mot de passe (min. 6 car.) *" type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} data-testid="new-user-password" />
            <div>
              <label className="text-xs text-[#577083] mb-1 block">Niveau d'accès</label>
              <Select value={form.role} onValueChange={(val) => setForm({...form, role: val})}>
                <SelectTrigger data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[200]">
                  {ROLE_OPTIONS.map(r => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label} — {r.desc}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Annuler</Button>
            <Button onClick={handleAdd} className="bg-[#1a2744] hover:bg-[#243459] text-[#e8d44d]" data-testid="submit-new-user">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password reset modal */}
      <Dialog open={showPwd} onOpenChange={setShowPwd}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader><DialogTitle>Mot de passe — {pwdUserName}</DialogTitle></DialogHeader>
          <div className="py-2">
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Nouveau mot de passe (min. 6 car.)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="admin-new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8fa4b5] hover:text-[#577083]"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPwd(false)}>Annuler</Button>
            <Button onClick={handlePasswordReset} className="bg-[#1a2744] hover:bg-[#243459] text-[#e8d44d]" data-testid="submit-admin-password">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
